import { ComponentProps, forwardRef } from 'react';
import { Button as AriaButton } from 'react-aria-components';

export type ButtonVariant = 'solid-fill' | 'outline' | 'text' | 'disabled';
export type ButtonSize = 'fl' | 'lg' | 'md' | 'sm' | 'xs';

export const buttonBaseStyle = `
  underline-offset-[calc(3/16*1rem)]
  focus-visible:outline focus-visible:outline-4 focus-visible:outline-black focus-visible:outline-offset-[calc(2/16*1rem)] focus-visible:ring-[calc(2/16*1rem)] focus-visible:ring-yellow-300
  aria-disabled:pointer-events-none aria-disabled:forced-colors:border-[GrayText] aria-disabled:forced-colors:text-[GrayText]
`;

export const buttonVariantStyle: { [key in ButtonVariant]: string } = {
  'solid-fill': `
    border-4
    border-double
    border-transparent
    bg-blue-900
    text-white
    hover:bg-blue-1000
    hover:underline
    active:bg-blue-1200
    active:underline
    aria-disabled:bg-solid-gray-300
    aria-disabled:text-solid-gray-50
  `,
  outline: `
    border
    border-current
    bg-white
    text-blue-900
    hover:bg-blue-200
    hover:text-blue-1000
    hover:underline
    active:bg-blue-300
    active:text-blue-1200
    active:underline
    aria-disabled:bg-white
    aria-disabled:text-solid-gray-300
  `,
  text: `
    text-blue-900
    underline
    hover:bg-blue-50
    hover:text-blue-1000
    hover:decoration-[calc(3/16*1rem)]
    active:bg-blue-100
    active:text-blue-1200
    focus-visible:bg-yellow-300
    aria-disabled:bg-transparent
    aria-disabled:focus-visible:bg-yellow-300
    aria-disabled:text-solid-gray-300
  `,
  disabled: 'bg-gray-300 text-gray-600 cursor-not-allowed',
};

export const buttonSizeStyle: { [key in ButtonSize]: string } = {
  fl: 'w-full min-h-14 rounded-lg px-4 py-3 text-oln-16B-100 flex justify-center items-center',
  lg: 'min-w-[calc(136/16*1rem)] min-h-14 rounded-lg px-4 py-3 text-oln-16B-100',
  md: 'min-w-24 min-h-12 rounded-lg px-4 py-2 text-oln-16B-100',
  sm: 'relative min-w-20 min-h-9 rounded-md px-3 py-0.5 text-oln-16B-100 after:absolute after:inset-x-0 after:-inset-y-full after:m-auto after:h-[44px]',
  xs: 'relative min-w-18 min-h-7 rounded px-2 py-0.5 text-oln-14B-100 after:absolute after:inset-x-0 after:-inset-y-full after:m-auto after:h-[44px]',
};

export type ButtonProps = {
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
  size: ButtonSize;
  onClick?: () => void;
  disabled?: boolean;
} & (
  | ({ asChild?: false } & ComponentProps<'button'>)
  | { asChild: true; children: React.ReactNode }
);

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const classNames = `${buttonBaseStyle} ${buttonSizeStyle[props.size]} ${
    props.variant ? buttonVariantStyle[props.variant] : ''
  } ${props.className ?? ''}`;

  return (
    <AriaButton
      type={props.type}
      className={classNames}
      onPress={props.onClick}
      isDisabled={props.disabled}
      ref={ref}
    >
      {props.children}
    </AriaButton>
  );
});

Button.displayName = 'Button';

export default Button;
